import React, { memo, useCallback, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import SlaGroupSelect from 'app/shared/components/SlaGroupSelect';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';

import { AI_CONFIRM_CARD_DEFAULT_LABELS } from './AiConfirmCard.constants';
import { aiConfirmCardPropTypes } from './AiConfirmCard.propTypes';
import {
  actionsStyles,
  reasonActionsStyles,
  reasonContainerStyles,
  slaGroupContainerStyles,
  slaGroupPromptStyles,
} from './AiConfirmCard.styles';
import { AiConfirmCardTestIds } from './AiConfirmCard.testIds';

const AiConfirmCard = ({
  onSubmit,
  onDismiss,
  entityId = null,
  isSubmitting = false,
  isSlaSelectionRequired = false,
  labels = AI_CONFIRM_CARD_DEFAULT_LABELS,
}) => {
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedSlaGroupId, setSelectedSlaGroupId] = useState(null);

  const reasonName = entityId ? `aiConfirmReason-${entityId}` : 'aiConfirmReason';

  const isApproveDisabled = isSlaSelectionRequired && !selectedSlaGroupId;

  const onDeclineOpen = useCallback(() => {
    setIsDeclineOpen(true);
  }, []);

  const onReasonChange = useCallback(event => {
    setReason(event.target.value);
  }, []);

  const onReasonCancel = useCallback(() => {
    setIsDeclineOpen(false);
    setReason('');
  }, []);

  const onReasonSubmit = useCallback(() => {
    const trimmed = reason.trim();

    onDismiss(trimmed === '' ? null : trimmed);
  }, [onDismiss, reason]);

  const onApproveClick = useCallback(() => {
    onSubmit(isSlaSelectionRequired ? selectedSlaGroupId : null);
  }, [isSlaSelectionRequired, onSubmit, selectedSlaGroupId]);

  if (isDeclineOpen) {
    return (
      <Box sx={reasonContainerStyles}>
        <TextFieldCDS
          fullWidth
          multiline
          ariaLabel={labels.reasonPlaceholder}
          data-testid={AiConfirmCardTestIds.REASON_INPUT}
          disabled={isSubmitting}
          minRows={2}
          name={reasonName}
          placeholder={labels.reasonPlaceholder}
          size="small"
          value={reason}
          onChange={onReasonChange}
        />
        <Box sx={reasonActionsStyles}>
          <Button
            data-testid={AiConfirmCardTestIds.REASON_CANCEL}
            disabled={isSubmitting}
            size="small"
            variant="line"
            onClick={onReasonCancel}
          >
            {labels.reasonCancel}
          </Button>
          <Button
            data-testid={AiConfirmCardTestIds.REASON_SUBMIT}
            disabled={isSubmitting}
            size="small"
            onClick={onReasonSubmit}
          >
            {labels.reasonSubmit}
          </Button>
          {isSubmitting ? <CircularProgress size={14} thickness={4} /> : null}
        </Box>
      </Box>
    );
  }

  return (
    <>
      {isSlaSelectionRequired ? (
        <Box sx={slaGroupContainerStyles}>
          <Typography sx={slaGroupPromptStyles} variant="regular12">
            {labels.slaGroupPrompt}
          </Typography>
          <SlaGroupSelect
            dataTestId={AiConfirmCardTestIds.SLA_GROUP_SELECT}
            disabled={isSubmitting}
            label={labels.slaGroupLabel}
            placeholder={labels.slaGroupPlaceholder}
            value={selectedSlaGroupId}
            onChange={setSelectedSlaGroupId}
          />
        </Box>
      ) : null}
      <Box sx={actionsStyles}>
        <Button
          data-testid={AiConfirmCardTestIds.DISMISS_BUTTON}
          disabled={isSubmitting}
          size="small"
          variant="line"
          onClick={onDeclineOpen}
        >
          {labels.dismiss}
        </Button>
        <Button
          data-testid={AiConfirmCardTestIds.APPROVE_BUTTON}
          disabled={isSubmitting || isApproveDisabled}
          size="small"
          onClick={onApproveClick}
        >
          {labels.approve}
        </Button>
        {isSubmitting ? <CircularProgress size={14} thickness={4} /> : null}
      </Box>
    </>
  );
};

AiConfirmCard.propTypes = aiConfirmCardPropTypes;

export default memo(AiConfirmCard);
